import React, { useState } from 'react';
import './premiumBillingTheme.css';
import { IconButton, Skeleton } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import TableRestaurantTwoToneIcon from '@mui/icons-material/TableRestaurantTwoTone';
import CategoryEmptyAnimation from '../Gif/CategoryEmptyAnimation';
import brokenImg from '../image/picture.png';
import dummyProductItemImg from '../../../../../Assets/BillingImages/emptyFoodImg.svg';
import emptyCartItemImg from '../../../../../Assets/BillingImages/emptyCart.svg';

const PremiumBillingTheme = ({
  // Product grid props
  productList,
  tableLoading,
  handleProductClick,
  
  // Cart props
  cartItems,
  handleIncrement,
  handleDecrement,
  handleDeleteCartItem,
  handleNoteClick,
  
  // Totals
  subTotal,
  taxTotal,
  grandTotal,
  decimalPosition,
  
  // Category props
  categoryList,
  selectedCategory,
  handleCategoryClick,
  categoryLoading,
  
  // Subcategory props
  subCategoryList,
  selectedSubCategory,
  handleSubCategoryClick,
  subCategoryLoading,
  
  // Special items
  specialItemsOpen,
  toggleSpecialItems,
  specialItemsList,
  specialItemCat,
  handleSpecialItemClick,
  canUseSpecialItems,
  
  // Veg/Non-veg filter
  vegFilter,
  handleVegFilterClick,
  
  // Checkout
  onCheckout,
  onAddOrder,
  onHold,
  onQuickBill,
  onTransferTable,
  singleOrderInfo,
  billType,
  floors,
}) => {
  
  // State for cart view toggle
  const [isCompactView, setIsCompactView] = useState(false);
  
  const handleImageError = (e) => {
    e.target.src = brokenImg;
  };

  return (
    <div className="premium-billing-container">
      {/* Left Side - Categories & Products */}
      <div className="premium-products-section">
        {/* Main Category Carousel */}
        <div className="premium-category-carousel">
          <div className="category-carousel-scroll">
            {categoryLoading ? (
              [...Array(8)].map((_, i) => (
                <div key={i} className="category-card skeleton">
                  <Skeleton variant="rectangular" width={90} height={90} style={{ borderRadius: '12px' }} />
                  <Skeleton variant="text" width={80} height={16} style={{ marginTop: 8 }} />
                </div>
              ))
            ) : categoryList?.length > 0 ? (
              categoryList.map((cat, i) => (
                <div
                  key={i}
                  className={`category-card ${selectedCategory === cat._id ? 'active' : ''}`}
                  onClick={() => handleCategoryClick(cat)}
                >
                  <div className="category-card-image">
                    <img src={cat?.imageUrl || brokenImg} alt={cat?.categoryName} onError={handleImageError} />
                  </div>
                  <p className="category-card-name">{cat?.categoryName}</p>
                </div>
              ))
            ) : (
              <div className="premium-empty-state">
                <CategoryEmptyAnimation />
                <p>No categories</p>
              </div>
            )}
          </div>
        </div>

        {/* Products Grid with Sidebar */}
        <div className="premium-products-wrapper">
          {/* Left Sidebar - Filters & Subcategories */}
          <div className="premium-sidebar">
            {/* Special Items */}
            <div className={`sidebar-section ${!canUseSpecialItems ? 'sidebar-section-disabled' : ''}`}>
              <div className="sidebar-section-header" onClick={canUseSpecialItems ? toggleSpecialItems : undefined} style={{ cursor: canUseSpecialItems ? 'pointer' : 'not-allowed' }}>
                <div className="sidebar-header-content">
                  <LocalOfferIcon sx={{ color: canUseSpecialItems ? "#667eea" : "#bdbdbd", fontSize: "1.2rem" }} />
                  <span>Special Items</span>
                </div>
                {canUseSpecialItems && (specialItemsOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />)}
              </div>
              {!canUseSpecialItems && (
                <p className="sidebar-disabled-text">Not enabled for this device</p>
              )}
              {canUseSpecialItems && specialItemsOpen && (
                <div className="sidebar-menu">
                  <div
                    className={`sidebar-menu-item ${specialItemCat === 'combo' ? 'active' : ''}`}
                    onClick={() => handleSpecialItemClick({ _id: 'combo' })}
                  >
                    COMBO OFFERS
                  </div>
                  {specialItemsList?.map((item, i) => (
                    <div
                      key={i}
                      className={`sidebar-menu-item ${specialItemCat === item._id ? 'active' : ''}`}
                      onClick={() => handleSpecialItemClick(item)}
                    >
                      {item.name}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Veg/Non-veg Filter */}
            <div className="sidebar-section">
              <div className="veg-filter-buttons">
                <button
                  className={`veg-filter-btn ${vegFilter === 'non-veg' ? 'active non-veg' : ''}`}
                  onClick={() => handleVegFilterClick('non-veg')}
                >
                  Non-Veg
                </button>
                <button
                  className={`veg-filter-btn ${vegFilter === 'veg' ? 'active veg' : ''}`}
                  onClick={() => handleVegFilterClick('veg')}
                >
                  Veg
                </button>
              </div>
            </div>

            {/* Subcategories */}
            {subCategoryList && subCategoryList.length > 0 && (
              <div className="sidebar-section">
                <div className="sidebar-section-title">Subcategories</div>
                <div className="sidebar-menu">
                  {subCategoryLoading ? (
                    [...Array(5)].map((_, i) => (
                      <Skeleton key={i} variant="rectangular" width="100%" height={36} style={{ marginBottom: 4, borderRadius: 8 }} />
                    ))
                  ) : (
                    subCategoryList.map((subCat, i) => (
                      <div
                        key={i}
                        className={`sidebar-menu-item ${selectedSubCategory === subCat._id ? 'active' : ''}`}
                        onClick={() => handleSubCategoryClick(subCat)}
                      >
                        {subCat.name}
                      </div>
                    ))
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Products Grid */}
          <div className="premium-products-grid-wrapper">
            <div className="premium-products-grid">
              {tableLoading ? (
                [...Array(12)].map((_, i) => (
                  <div key={i} className="premium-product-card skeleton">
                    <Skeleton variant="rectangular" width="100%" height={140} />
                    <Skeleton variant="text" width="80%" height={24} style={{ marginTop: 12 }} />
                    <Skeleton variant="text" width="60%" height={20} />
                  </div>
                ))
              ) : productList?.length > 0 ? (
                productList.map((product, i) => (
                  <div
                    key={i}
                    className={`premium-product-card ${specialItemCat ? 'special-offer-card' : ''}`}
                    onClick={() => handleProductClick(product)}
                  >
                    <div className="product-card-image-wrapper">
                      <img
                        src={product?.imageUrl || dummyProductItemImg}
                        alt={product?.productName}
                        onError={handleImageError}
                        className="product-card-image"
                      />
                      {specialItemCat && (
                        <>
                          <div className={`special-offer-price-tag ${product?.offerType || 'default'}`}>
                            <LocalOfferIcon fontSize="small" />
                            <span>₹{(product?.sellingPrice || product?.salesPrice || product?.price || 0).toFixed(0)}</span>
                          </div>
                          {product?.offerType && (
                            <div className={`offer-type-badge ${product.offerType.toLowerCase()}`}>
                              {product.offerType === 'SPLASH' && '💦'}
                              {product.offerType === 'HOT' && '🔥'}
                              {product.offerType === 'NEW' && '✨'}
                              {product.offerType === 'SPECIAL' && '⭐'}
                              <span>{product.offerType}</span>
                            </div>
                          )}
                        </>
                      )}
                      {product?.dayName && specialItemCat && (
                        <div className="special-offer-day-badge">
                          <span className="star-icon">★</span>
                          <span>{product.dayName}</span>
                        </div>
                      )}
                    </div>
                    <div className="product-card-content">
                      <h4 className="product-card-name">{product?.productName}</h4>
                      <div className="product-card-footer">
                        <span className="product-card-price">
                          ₹{(product?.sellingPrice || product?.salesPrice || product?.price || 0).toFixed(typeof decimalPosition === 'number' ? decimalPosition : 2)}
                        </span>
                        <button className="product-add-btn">
                          <AddIcon fontSize="small" />
                        </button>
                      </div>
                    </div>
                  </div>
                ))
              ) : (
                <div className="premium-empty-state">
                  <CategoryEmptyAnimation />
                  <p>No products available</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Right Side - Cart */}
      <div className="premium-cart-section">
        <div className="premium-cart-header">
          <div className="cart-header-left">
            <h3>{singleOrderInfo?.name || "Current Order"}</h3>
            <div className="cart-view-toggle">
              <div 
                className={`toggle-switch ${isCompactView ? 'active' : ''}`}
                onClick={() => setIsCompactView(!isCompactView)}
              >
                <div className="toggle-slider"></div>
              </div>
            </div>
          </div>
          <div className="cart-header-right">
            {singleOrderInfo && singleOrderInfo.tableNames && singleOrderInfo.tableNames.length > 0 ? (
              <button className="premium-table-btn" onClick={onTransferTable} title="Transfer Table">
                <TableRestaurantTwoToneIcon fontSize="small" />
                <span>
                  {singleOrderInfo.tableNames.map((name, index) => (
                    <React.Fragment key={index}>
                      {index > 0 && index === singleOrderInfo.tableNames.length - 1 && singleOrderInfo.tableNames.length > 1 && " & "}
                      {index > 0 && index < singleOrderInfo.tableNames.length - 1 && ", "}
                      {name}
                    </React.Fragment>
                  ))}
                  {singleOrderInfo.chairId && singleOrderInfo.chairId.length > 0 && `/CH-${singleOrderInfo.chairId.length}`}
                </span>
              </button>
            ) : (
              cartItems?.length > 0 && billType === "DINEIN" && floors?.length > 0 && (
                <button className="premium-table-btn" onClick={onTransferTable} title="Add Table">
                  <span>Add</span>
                  <TableRestaurantTwoToneIcon fontSize="small" />
                </button>
              )
            )}
            <span className="cart-item-count">{cartItems?.length || 0} items</span>
          </div>
        </div>

        <div className="premium-cart-items">
          {cartItems?.length > 0 ? (
            cartItems.map((item, i) => (
              <div key={i} className={`premium-cart-item ${isCompactView ? 'compact-view' : ''}`}>
                {/* Top Right Icons */}
                <div className="cart-item-top-icons">
                  <button
                    className="add-note-btn"
                    onClick={(e) => { e.stopPropagation(); handleNoteClick(i); }}
                    title={item?.customerNote ? 'Edit Note' : 'Add Note'}
                  >
                    <i className="bi bi-sticky"></i>
                  </button>
                  <button
                    className="cart-item-delete-btn"
                    onClick={(e) => { e.stopPropagation(); handleDeleteCartItem(i); }}
                    title="Delete Item"
                  >
                    <DeleteOutlineIcon fontSize="small" />
                  </button>
                </div>

                {/* Top Row - Image and Details */}
                <div className="cart-item-top-row">
                  <div className="cart-item-image">
                    <img
                      src={item?.imageUrl || dummyProductItemImg}
                      alt={item?.productName}
                      onError={handleImageError}
                    />
                  </div>
                  <div className="cart-item-details">
                    <h5 className="cart-item-name">{item?.productName}</h5>
                    <p className="cart-item-price">
                      ₹{Number(item?.salesPrice || 0).toFixed(decimalPosition || 2)}
                    </p>
                    {item?.customerNote && (
                      <div className="cart-item-note-item">
                        <i className="bi bi-sticky"></i>
                        <span>{item.customerNote}</span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Bottom Row - Quantity Controls and Total */}
                <div className="cart-item-bottom-row">
                  <div className="quantity-controls">
                    <IconButton
                      size="small"
                      onClick={(e) => { e.stopPropagation(); handleDecrement(i); }}
                      className="qty-btn"
                    >
                      <RemoveIcon fontSize="small" />
                    </IconButton>
                    <span className="quantity-display">{item?.quantity || 0}</span>
                    <IconButton
                      size="small"
                      onClick={(e) => { e.stopPropagation(); handleIncrement(i); }}
                      className="qty-btn"
                    >
                      <AddIcon fontSize="small" />
                    </IconButton>
                  </div>
                  <div className="cart-item-total">
                    ₹{Number((item?.quantity || 0) * (item?.salesPrice || 0)).toFixed(decimalPosition || 2)}
                  </div>
                </div>
              </div>
            ))
          ) : (
            <div className="premium-empty-cart">
              <img src={emptyCartItemImg} alt="Empty cart" />
              <p>Your cart is empty</p>
              <span>Add items to get started</span>
            </div>
          )}
        </div>

        {cartItems?.length > 0 && (
          <div className="premium-cart-summary">
            <div className="summary-row">
              <span>Subtotal</span>
              <span>₹{Number(subTotal || 0).toFixed(decimalPosition)}</span>
            </div>
            <div className="summary-row">
              <span>Tax</span>
              <span>₹{Number(taxTotal || 0).toFixed(decimalPosition)}</span>
            </div>
            <div className="summary-row total">
              <span>Total</span>
              <span>₹{Number(grandTotal || 0).toFixed(decimalPosition)}</span>
            </div>
            <div style={{ display: 'flex', gap: '8px', marginTop: '12px' }}>
              <button 
                className="premium-hold-btn" 
                onClick={onHold}
                style={{ flex: 1 }}
                title="Hold Order"
              >
                Hold
              </button>
              <button 
                className="premium-add-order-btn" 
                onClick={onAddOrder}
                style={{ flex: 1 }}
              >
                {singleOrderInfo?.status === "DRAFT" ? "Update" : "Add Order"}
              </button>
            </div>
            <div style={{ display: 'flex', gap: '8px', marginTop: '8px' }}>
              <button 
                className="premium-checkout-btn" 
                onClick={onCheckout}
                style={{ flex: 1 }}
              >
                Payment
              </button>
              <button 
                className="premium-quick-bill-btn" 
                onClick={onQuickBill}
                disabled={singleOrderInfo?.status === "DRAFT"}
                style={{ flex: 1 }}
                title="Quick Bill"
              >
                Quick Bill
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PremiumBillingTheme;
